import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";

import { useCallback, useState } from "react";

import { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { ALWAYS_ENABLE_PRICE_CHART } from "components/EntityAnalyticsWidget";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";
import publicUserPriceChart from "assets/public-user-price-chart.svg";

const EntityAnalyticsChart = dynamic(
  () => import("components/EntityAnalyticsChart")
);
const EntityStatsCard = dynamic(() => import("components/EntityStatsCard"));

const EntitySnapshotWidget = ({
  entity,
  containerClassName,
  onEmpty,
  customHeader,
}: {
  entity: Entity;
  containerClassName?: string;
  onEmpty?: () => void;
  customHeader?: React.ReactNode;
}) => {
  const { authenticated } = useAuthentication();
  const { toggleModal } = useSignInSignUp();

  const { slug } = entity || {};

  const [snapshotEmpty, setSnapshotEmpty] = useState(false);
  const [statsEmpty, setStatsEmpty] = useState(false);

  const placeholderChart = useCallback(() => {
    return (
      <>
        <h3 className="text-[16px] font-medium">
          Snapshot
          <span
            className="ml-[5px] font-normal text-[#b5485d] hover:text-[#b5485d] cursor-pointer"
            onClick={() => toggleModal("sign-up")}
          >
            (Sign Up to Access)
          </span>
        </h3>
        <div className="p-[5px]">
          <Image
            src={publicUserPriceChart}
            alt="analytics-chart"
            className="w-full h-fit"
            priority
          />
        </div>
      </>
    );
  }, [toggleModal]);

  if (!entity) return <></>;

  if (authenticated || ALWAYS_ENABLE_PRICE_CHART) {
    if (snapshotEmpty && statsEmpty) return <></>;
    return (
      <CustomCard
        className={twMerge(
          "px-[15px] py-[15px] shadow-none md:shadow-md mb-0 pb-0 md:pb-[15px] md:mb-[30px]",
          containerClassName
        )}
      >
        {customHeader}
        {authenticated ? (
          <>
            <h3 className="text-[16px] font-medium">Snapshot</h3>
            {!snapshotEmpty && (
              <EntityAnalyticsChart
                entity={entity}
                onEmpty={() => {
                  setSnapshotEmpty(true);
                  onEmpty?.();
                }}
              />
            )}
          </>
        ) : (
          placeholderChart()
        )}

        <EntityStatsCard entity={entity} onEmpty={() => setStatsEmpty(true)} />
        <div className="px-[5px] mt-[5px] ">
          <Link
            href={`/entities/${slug}/analytics`}
            className="text-[14px] font-bold text-smart-teal hover:text-dark-teal"
          >
            {"View More »"}
          </Link>
        </div>
      </CustomCard>
    );
  }

  return (
    <CustomCard className="px-[15px] py-[15px]">
      {placeholderChart()}
      <EntityStatsCard entity={entity} />
    </CustomCard>
  );
};

export default EntitySnapshotWidget;
